<template>
    <div class="program-table-container">
        <!-- Controls -->
        <div class="table-controls mb-3">
            <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div class="d-flex align-items-center gap-2">
                    <label class="text-muted small me-2">Per page:</label>
                    <select v-model="perPage" @change="handlePerPageChange" class="form-select form-select-sm" style="width: auto;">
                        <option :value="25">25</option>
                        <option :value="50">50</option>
                        <option :value="100">100</option>
                    </select>
                </div>
                <div class="d-flex align-items-center gap-3">
                    <span class="text-muted small">
                        Showing {{ startIndex + 1 }}-{{ endIndex }} of {{ totalPrograms }} programs
                    </span>
                    <button
                        class="compass-btn compass-btn--outline compass-btn--sm"
                        @click="exportData"
                    >
                        Export CSV
                    </button>
                </div>
            </div>
        </div>

        <!-- Table -->
        <div class="table-responsive">
            <table class="compass-table analytics-table">
                <thead>
                    <tr>
                        <th class="rank-col">#</th>
                        <th class="sortable" @click="sort('program')">
                            Program
                            <span v-if="sortField === 'program'" class="sort-icon">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
                        </th>
                        <th class="text-end sortable metric-col" @click="sort('tx_count')">
                            Txns
                            <span v-if="sortField === 'tx_count'" class="sort-icon">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
                        </th>
                        <th class="text-end sortable metric-col" @click="sort('fee')">
                            Fees
                            <span v-if="sortField === 'fee'" class="sort-icon">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
                        </th>
                        <th class="text-end sortable metric-col" @click="sort('revenue')">
                            Revenue
                            <span v-if="sortField === 'revenue'" class="sort-icon">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
                        </th>
                        <th class="text-end sortable metric-col" @click="sort('tips')">
                            Tips
                            <span v-if="sortField === 'tips'" class="sort-icon">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
                        </th>
                        <th class="text-end sortable metric-col" @click="sort('users')">
                            Users
                            <span v-if="sortField === 'users'" class="sort-icon">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
                        </th>
                        <th class="text-end sortable metric-col" @click="sort('compute')">
                            Compute
                            <span v-if="sortField === 'compute'" class="sort-icon">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
                        </th>
                        <th class="text-center status-col">Jito %</th>
                        <th class="text-end">Reverted %</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="(p, idx) in programs"
                        :key="p.program"
                        class="program-row"
                        @click="navigateToProgram(p.program)"
                    >
                        <td class="rank-col">
                            <span class="rank-badge">{{ startIndex + idx + 1 }}</span>
                        </td>
                        <td class="program-cell">
                            <div class="program-info">
                                <img
                                    v-if="p.logo"
                                    :src="p.logo"
                                    :alt="p.name || 'Program'"
                                    class="program-logo"
                                    @error="$event.target.style.display='none'"
                                />
                                <div class="program-details">
                                    <a
                                        :href="`/analytics/programs/${p.program}`"
                                        class="program-link text-gradient"
                                        :title="p.program"
                                        @click.stop
                                    >
                                        {{ p.name || formatPubkey(p.program) }}
                                    </a>
                                    <span v-if="p.name" class="program-address text-muted">
                                        {{ formatPubkey(p.program) }}
                                    </span>
                                </div>
                            </div>
                            <a
                                :href="`https://solscan.io/account/${p.program}`"
                                target="_blank"
                                class="external-link"
                                @click.stop
                                title="View on Solscan"
                            >↗</a>
                        </td>
                        <td class="text-end metric-cell">
                            <div class="metric-bar-container">
                                <div
                                    class="metric-bar metric-bar--purple"
                                    :style="{ width: getBarWidth(p.metrics.totalTransactions, maxTx) + '%' }"
                                ></div>
                                <span class="metric-value font-mono">{{ formatCompact(p.metrics.totalTransactions) }}</span>
                            </div>
                        </td>
                        <td class="text-end metric-cell">
                            <div class="metric-bar-container">
                                <div
                                    class="metric-bar metric-bar--pink"
                                    :style="{ width: getBarWidth(p.metrics.totalFees, maxFees) + '%' }"
                                ></div>
                                <span class="metric-value font-mono">{{ p.metrics.totalFees.toFixed(2) }}</span>
                            </div>
                        </td>
                        <td class="text-end metric-cell">
                            <span class="metric-value font-mono text-gradient">{{ p.metrics.totalRevenue.toFixed(2) }}</span>
                        </td>
                        <td class="text-end metric-cell">
                            <span class="metric-value font-mono">{{ p.metrics.totalTips.toFixed(3) }}</span>
                        </td>
                        <td class="text-end metric-cell">
                            <div class="metric-bar-container">
                                <div
                                    class="metric-bar metric-bar--cyan"
                                    :style="{ width: getBarWidth(p.metrics.uniqueUsers, maxUsers) + '%' }"
                                ></div>
                                <span class="metric-value font-mono">{{ formatCompact(p.metrics.uniqueUsers) }}</span>
                            </div>
                        </td>
                        <td class="text-end metric-cell">
                            <div class="metric-bar-container">
                                <div
                                    class="metric-bar metric-bar--amber"
                                    :style="{ width: getBarWidth(p.metrics.totalCompute, maxCompute) + '%' }"
                                ></div>
                                <span class="metric-value font-mono">{{ formatCompute(p.metrics.totalCompute) }}</span>
                            </div>
                        </td>
                        <td class="text-center">
                            <span :class="getJitoClass(p.metrics.jitoPercent || 0)">
                                {{ (p.metrics.jitoPercent || 0).toFixed(1) }}%
                            </span>
                        </td>
                        <td class="text-end">
                            <span :class="getRevertedClass(p.metrics.revertedPercent || 0)">
                                {{ (p.metrics.revertedPercent || 0).toFixed(1) }}%
                            </span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Pagination -->
        <div class="pagination-controls mt-3">
            <div class="d-flex justify-content-center align-items-center gap-2">
                <button
                    class="compass-btn compass-btn--outline compass-btn--sm"
                    :disabled="currentPage === 1"
                    @click="goToPage(1)"
                >
                    First
                </button>
                <button
                    class="compass-btn compass-btn--outline compass-btn--sm"
                    :disabled="currentPage === 1"
                    @click="goToPage(currentPage - 1)"
                >
                    Prev
                </button>

                <span class="page-info text-muted">
                    Page {{ currentPage }} of {{ totalPages }}
                </span>

                <button
                    class="compass-btn compass-btn--outline compass-btn--sm"
                    :disabled="currentPage === totalPages"
                    @click="goToPage(currentPage + 1)"
                >
                    Next
                </button>
                <button
                    class="compass-btn compass-btn--outline compass-btn--sm"
                    :disabled="currentPage === totalPages"
                    @click="goToPage(totalPages)"
                >
                    Last
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { formatCompact, formatCompute, formatPubkey, getRevertedClass, getJitoClass } from '../../composables/useFormatters';

export default {
    name: 'ProgramTable',

    props: {
        programs: {
            type: Array,
            required: true
        },
        totalPrograms: {
            type: Number,
            default: 0
        },
        loading: {
            type: Boolean,
            default: false
        },
        initialPage: {
            type: Number,
            default: 1
        },
        initialPerPage: {
            type: Number,
            default: 25
        },
        initialSortField: {
            type: String,
            default: 'tx_count'
        },
        initialSortDir: {
            type: String,
            default: 'desc'
        }
    },

    emits: ['page-change', 'sort-change', 'per-page-change'],

    methods: {
        formatCompact,
        formatCompute,
        formatPubkey,
        getRevertedClass,
        getJitoClass,

        getBarWidth(value, max) {
            if (!max || !value) return 0;
            return Math.min((value / max) * 100, 100);
        },

        sort(field) {
            if (this.sortField === field) {
                this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
            } else {
                this.sortField = field;
                this.sortDir = 'desc';
            }
            this.currentPage = 1;
            this.$emit('sort-change', { field: this.sortField, dir: this.sortDir });
        },

        goToPage(page) {
            if (page < 1 || page > this.totalPages) return;
            this.currentPage = page;
            this.$emit('page-change', { page: this.currentPage, perPage: this.perPage });
            this.updateUrl();
        },

        handlePerPageChange() {
            this.currentPage = 1;
            this.$emit('per-page-change', { perPage: this.perPage });
            this.updateUrl();
        },

        updateUrl() {
            const url = new URL(window.location.href);
            url.searchParams.set('page', this.currentPage);
            url.searchParams.set('per_page', this.perPage);
            window.history.pushState({}, '', url);
        },

        navigateToProgram(address) {
            window.location.href = `/analytics/programs/${address}`;
        },

        exportData() {
            const headers = ['Rank', 'Program', 'Transactions', 'Fees (SOL)', 'Revenue (SOL)', 'Tips (SOL)', 'Users', 'Compute', 'Jito %', 'Reverted %'];
            const rows = this.programs.map((p, idx) => [
                this.startIndex + idx + 1,
                p.program,
                p.metrics.totalTransactions,
                p.metrics.totalFees.toFixed(4),
                p.metrics.totalRevenue.toFixed(4),
                p.metrics.totalTips.toFixed(4),
                p.metrics.uniqueUsers,
                p.metrics.totalCompute,
                (p.metrics.jitoPercent || 0).toFixed(2),
                (p.metrics.revertedPercent || 0).toFixed(2)
            ]);

            const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
            const blob = new Blob([csv], { type: 'text/csv' });
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = `programs_page${this.currentPage}_${new Date().toISOString().split('T')[0]}.csv`;
            a.click();
        }
    },

    data() {
        return {
            currentPage: this.initialPage,
            perPage: this.initialPerPage,
            sortField: this.initialSortField,
            sortDir: this.initialSortDir
        };
    },

    computed: {
        totalPages() {
            return Math.ceil(this.totalPrograms / this.perPage) || 1;
        },
        startIndex() {
            return (this.currentPage - 1) * this.perPage;
        },
        endIndex() {
            return Math.min(this.startIndex + this.programs.length, this.totalPrograms);
        },
        // Calculate max values for bar scaling
        maxTx() {
            return Math.max(...this.programs.map(p => p.metrics.totalTransactions), 1);
        },
        maxFees() {
            return Math.max(...this.programs.map(p => p.metrics.totalFees), 1);
        },
        maxUsers() {
            return Math.max(...this.programs.map(p => p.metrics.uniqueUsers), 1);
        },
        maxCompute() {
            return Math.max(...this.programs.map(p => p.metrics.totalCompute), 1);
        }
    },

    mounted() {
        // Read page from URL on mount
        const params = new URLSearchParams(window.location.search);
        if (params.has('page')) {
            this.currentPage = parseInt(params.get('page')) || 1;
        }
        if (params.has('per_page')) {
            this.perPage = parseInt(params.get('per_page')) || 25;
        }
    }
};
</script>

<style scoped>
.program-table-container {
    width: 100%;
}

.analytics-table {
    width: 100%;
}

.analytics-table th.sortable {
    cursor: pointer;
    user-select: none;
    transition: color 0.2s;
}

.analytics-table th.sortable:hover {
    color: var(--compass-purple, #8b5cf6);
}

.sort-icon {
    margin-left: 4px;
    font-size: 0.8em;
    color: var(--compass-purple, #8b5cf6);
}

.rank-col {
    width: 50px;
    text-align: center;
}

.rank-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    background: linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(236, 72, 153, 0.2));
    border-radius: 6px;
    font-size: 0.8rem;
    font-weight: 600;
    color: #9ca3af;
}

.program-row {
    cursor: pointer;
    transition: background-color 0.2s;
}

.program-row:hover {
    background-color: rgba(139, 92, 246, 0.05);
}

.program-cell {
    display: flex;
    align-items: center;
    gap: 8px;
}

.program-info {
    display: flex;
    align-items: center;
    gap: 10px;
}

.program-logo {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
}

.program-details {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.program-link {
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.program-address {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.7rem;
    opacity: 0.7;
}

.external-link {
    color: #6b7280;
    font-size: 0.75rem;
    text-decoration: none;
    opacity: 0.6;
    transition: opacity 0.2s;
}

.external-link:hover {
    opacity: 1;
}

.metric-col {
    min-width: 100px;
}

.status-col {
    min-width: 80px;
}

.metric-cell {
    position: relative;
}

.metric-bar-container {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    height: 24px;
}

.metric-bar {
    position: absolute;
    left: 0;
    top: 2px;
    bottom: 2px;
    border-radius: 4px;
    opacity: 0.3;
    transition: width 0.3s ease;
}

.metric-bar--purple {
    background: var(--compass-purple, #8b5cf6);
}

.metric-bar--pink {
    background: var(--compass-pink, #ec4899);
}

.metric-bar--cyan {
    background: var(--compass-cyan, #10f7cf);
}

.metric-bar--amber {
    background: #f59e0b;
}

.metric-value {
    position: relative;
    z-index: 1;
    font-size: 0.9rem;
}

.font-mono {
    font-family: 'JetBrains Mono', monospace;
}

.compass-btn--sm {
    padding: 0.3rem 0.6rem;
    font-size: 0.8rem;
}

.page-info {
    padding: 0 1rem;
    font-size: 0.9rem;
}

/* Status classes */
.text-gradient {
    background: linear-gradient(135deg, var(--compass-purple, #8b5cf6), var(--compass-pink, #ec4899));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.text-success {
    color: var(--compass-success, #22c55e) !important;
}

.text-warning {
    color: #f59e0b !important;
}

.text-danger {
    color: var(--compass-danger, #ef4444) !important;
}

.text-info {
    color: #3b82f6 !important;
}

.text-muted {
    color: #6b7280 !important;
}
</style>
